/**
 * Canonical color variants for timeline tick/label text. Maps semantic
 * variants to design-token colors that are already tuned for contrast in
 * both light and dark mode. Consumers should import `TIMELINE_INK` (not
 * define their own palette) so theme parity is structural.
 *
 * - `amber` / `red` route through the `-text` variants of --primary and
 *   --destructive because full-strength --primary / --destructive fail AA
 *   on light-mode warm surfaces (see the help-sheet audit 2026-04-11).
 * - `green` uses --scenario-green directly (already AA-tuned).
 * - `muted` is a neutral fade of the foreground token.
 * - `label` intentionally returns `undefined` so the caller inherits the
 *   surrounding text color — used for plain axis-label ticks.
 */
export type TimelineInkVariant = 'label' | 'amber' | 'red' | 'green' | 'muted';

export const TIMELINE_INK: Record<TimelineInkVariant, string | undefined> = {
  label: undefined,
  amber: 'var(--primary-text)',
  red: 'var(--destructive-text)',
  green: 'var(--scenario-green)',
  muted: 'color-mix(in srgb, var(--color-foreground), transparent 10%)',
};

export const TIMELINE_NOTE_TEXT_CLASS =
  'font-sans text-[10px] sm:text-[12px] leading-[1.5] text-foreground/86';

export const TIMELINE_ANNOTATION_TEXT_CLASS = `${TIMELINE_NOTE_TEXT_CLASS} text-center`;

export const TIMELINE_ANNOTATION_INLINE_CLASS = `${TIMELINE_ANNOTATION_TEXT_CLASS} mt-1 mb-2`;

export const TIMELINE_ANNOTATION_BLOCK_CLASS = `${TIMELINE_ANNOTATION_TEXT_CLASS} mt-2 mb-3`;

export const TIMELINE_CAPTION_TEXT_CLASS = 'text-[10px] sm:text-[12px] text-foreground/86';

export const TIMELINE_AXIS_LABEL_CLASS =
  'font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.4px] text-foreground/86';

export const TIMELINE_AXIS_TICK_CLASS =
  'font-mono text-[10px] sm:text-[12px] leading-[1.15] font-medium text-foreground/86 tabular-nums';

export const TIMELINE_AXIS_UNIT_CLASS =
  'font-mono text-[10px] sm:text-[12px] leading-[1.1] text-foreground/82';

export const TIMELINE_SEGMENT_HEADING_CLASS =
  'font-heading text-[10px] sm:text-[12px] uppercase tracking-[1.2px] text-foreground/80 font-semibold';

export function formatDeadlineTick(hours: number): string {
  return `${hours}h\ndeadline`;
}
